import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import WatchlistClient from '../client/watchlist/WatchlistClient';
import {
  WatchlistBaseModel,
  WatchlistConfiguration,
  WatchlistEntityRequest,
  WatchlistGeneralRequest,
  WatchlistIndividualRequest,
  WatchlistListsModel,
  SafeListConfig,
  SafeListRequest,
  WatchlistByIdResponse,
} from '../domain/watchlist/WatchlistDomains';
import { WatchlistType } from '../constant/Constants';
import { query } from '@trlogic/client-js';
import { AlertCommentLog, AlertStatus, WatchlistAlertDomains } from '../domain/watchlist/WatchlistAlertDomains';
import { WatchlistNewsDomains } from '../domain/watchlist/WatchlistNewsDomains';
import MessageUtil from '../util/MessageUtil';
import React from 'react';
import { CustomerRequest, WatchlistCustomerDomains } from '../domain/watchlist/WatchlistCustomerDomains';
import { CancelToken } from 'axios';
import { MatchManyLogConfig, MatchManyLogCount } from '../domain/watchlist/WatchlistHistoryDomains';

class WatchlistService {
  private watchlistClient: WatchlistClient = GatewayContainer.getInstance().get(GatewayNames.WATCHLIST_CLIENT);

  public searchMany(
    request: Partial<WatchlistIndividualRequest | WatchlistEntityRequest | WatchlistGeneralRequest>,
    type: WatchlistType,
  ): Promise<WatchlistBaseModel[]> {
    return this.watchlistClient.searchMany(request, type);
  }

  public searchOne(
    request: Partial<WatchlistIndividualRequest | WatchlistEntityRequest | WatchlistGeneralRequest>,
    type: WatchlistType,
  ): Promise<WatchlistBaseModel> {
    return this.watchlistClient.searchOne(request, type);
  }

  public getById(type: WatchlistType, id: string): Promise<WatchlistByIdResponse> {
    return this.watchlistClient.getById(type, id);
  }

  public getIndividualId(listId: string, relationId: string): Promise<WatchlistByIdResponse> {
    return this.watchlistClient.getIndividualId(listId, relationId);
  }

  public getLists(): Promise<WatchlistListsModel[]> {
    return this.watchlistClient.getLists();
  }

  public getKycLogCount(index: string, _query: query.SelectQuery): Promise<number> {
    return this.watchlistClient.getKycLogCount(index, _query);
  }

  public goKycLog(index: string, id: string): Promise<void> {
    return this.watchlistClient.goKycLog(index, id);
  }

  public closeKycLog(index: string, id: string, type: AlertStatus, reason?: string): Promise<void> {
    return this.watchlistClient.closeKycLog(index, id, type, reason);
  }

  public closeKycLogBulk(index: string, ids: string[], type: AlertStatus, selectQuery?: query.SelectQuery): Promise<void> {
    return this.watchlistClient.closeKycLogBulk(index, ids, type, selectQuery);
  }

  public customerLookup(body: CustomerRequest): Promise<WatchlistCustomerDomains> {
    return this.watchlistClient.customerLookup(body);
  }

  public async getNews(name: string, region: string, exactMatch?: boolean): Promise<WatchlistNewsDomains> {
    try {
      return await this.watchlistClient.getNews(name, region, exactMatch);
    } catch (error) {
      MessageUtil.error({
        summary: 'News',
        detail: <div>Server Error</div>,
      });
      throw error;
    }
  }

  public async imageConvertBase64(url: string): Promise<string> {
    return await this.watchlistClient.imageConvertBase64(url);
  }

  public saveConfiguration(body: WatchlistConfiguration): Promise<void> {
    return this.watchlistClient.saveConfiguration(body);
  }

  public deleteConfiguration(id: string): Promise<void> {
    return this.watchlistClient.deleteConfiguration(id);
  }

  public sendComment(id: string, comment: string): Promise<AlertCommentLog> {
    return this.watchlistClient.sendComment(id, comment);
  }

  public getAlertCounts(): Promise<Record<string, number>> {
    return this.watchlistClient.getAlertCounts();
  }

  public getAlertPageData(
    type: WatchlistType,
    _query: query.SelectQuery,
    token: CancelToken,
  ): Promise<{
    data: WatchlistAlertDomains[];
    count: number;
  }> {
    return this.watchlistClient.getAlertPageData(type, _query, token);
  }

  public assignAlert(type: WatchlistType, id: string, assignee: string): Promise<void> {
    return this.watchlistClient.assignAlert(type, id, assignee);
  }

  public addSafeList(request: SafeListRequest): Promise<void> {
    return this.watchlistClient.addSafeList(request);
  }

  public removeSafeList(request: SafeListRequest): Promise<void> {
    return this.watchlistClient.removeSafeList(request);
  }

  public getSafeListConfig(): Promise<SafeListConfig> {
    return this.watchlistClient.getSafeListConfig();
  }

  public saveSafeListConfig(enabled: boolean): Promise<SafeListConfig> {
    return this.watchlistClient.saveSafeListConfig(enabled);
  }

  public getMatchManyLogCount(filter: query.Filter): Promise<MatchManyLogCount> {
    return this.watchlistClient.getMatchManyLogCount(filter);
  }

  public getMatchManyLogConfig(): Promise<MatchManyLogConfig> {
    return this.watchlistClient.getMatchManyLogConfig();
  }

  public saveMatchManyLogConfig(minScore: number, maxRecords: number): Promise<MatchManyLogConfig> {
    return this.watchlistClient.saveMatchManyLogConfig(minScore, maxRecords);
  }
}

export default WatchlistService;
